import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

/** Components */
import ProductDetailInfo from 'components/ProductDetail/ProductDetailInfo';
import ProductDetailImageGallery from 'components/ProductDetail/ProductDetailImageGallery';

import useResponsive from 'hooks/useResponsive';

import { getImageOrDefault } from 'utils/products';
import { transformImageUrl } from 'utils/functions';

import { RESOLUTION } from 'constants/image-resolution';

const ProductDetail = ({ product, productWSP = 0, loading = false }) => {
  const theme = useTheme();
  const [imagesGallery, setImagesGallery] = useState([]);
  const [imageToDisplay, setImageToDisplay] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);
  const babyGlideRef = useRef();

  const isMobile = useResponsive('down', 'sm');

  const GAP = '3rem';

  const onVariationChange = (variation) => {
    const variationThumbnail = variation.attributes.productThumbnailUrl;
    const thumbnailIndex = imagesGallery.findIndex((x) => {
      return (
        x.img ===
        transformImageUrl(
          variationThumbnail.productThumbnailBaseUrl,
          variationThumbnail.productThumbnailName,
          isMobile ? RESOLUTION.PDP.MOBILE : RESOLUTION.PDP.DESKTOP
        )
      );
    });

    if (thumbnailIndex !== -1) {
      setImageToDisplay(thumbnailIndex);
      babyGlideRef?.current?.goToSlide(thumbnailIndex);
    } else {
      setImageToDisplay(0);
      babyGlideRef?.current?.goToSlide(0);
    }
  };

  useEffect(() => {
    let allImages = [
      {
        img: product.productThumbnailUrl?.productThumbnailBaseUrl
          ? transformImageUrl(
              product.productThumbnailUrl?.productThumbnailBaseUrl,
              product.productThumbnailUrl?.productThumbnailName,
              isMobile ? RESOLUTION.PDP.MOBILE : RESOLUTION.PDP.DESKTOP
            )
          : getImageOrDefault({
              base: product.productThumbnailUrl?.productThumbnailBaseUrl,
              path: product.productThumbnailUrl?.productThumbnailName,
            }),
        title: product.productName,
      },
    ];

    // add product level images
    if (product?.productImageUrl && product?.productImageUrl[0]) {
      const parsedImages = product?.productImageUrl?.map((x) => ({
        img: x.productImageBaseUrl
          ? transformImageUrl(
              x.productImageBaseUrl,
              x.productImageName,
              isMobile ? RESOLUTION.PDP.MOBILE : RESOLUTION.PDP.DESKTOP
            )
          : getImageOrDefault({
              base: x.productImageBaseUrl,
              path: x.productImageName,
            }),
        title: product.productName,
      }));

      allImages = [...allImages, ...parsedImages];
    }

    // add variations' thumbnails
    const variationsThumbnails =
      product.variations && product.variations[0]
        ? product?.variations?.filter((x) => x.attributes.productThumbnailUrl?.productThumbnailName !== '')
        : [];

    // remove duplicate variations
    const uniqueVariations = variationsThumbnails.reduce(
      (acc, currentVariation) => {
        const thumbnailName = currentVariation.attributes.productThumbnailUrl?.productThumbnailName;

        if (thumbnailName && !acc.seen.has(thumbnailName)) {
          acc.seen.add(thumbnailName);
          acc.result.push(currentVariation);
        }

        return acc;
      },
      { seen: new Set(), result: [] }
    ).result;

    if (uniqueVariations?.length > 0) {
      const parsedImages = uniqueVariations?.map((x) => ({
        img: x.attributes.productThumbnailUrl?.productThumbnailBaseUrl
          ? transformImageUrl(
              x.attributes.productThumbnailUrl.productThumbnailBaseUrl,
              x.attributes.productThumbnailUrl.productThumbnailName,
              isMobile ? RESOLUTION.PDP.MOBILE : RESOLUTION.PDP.DESKTOP
            )
          : getImageOrDefault({
              base: x.attributes.productThumbnailUrl?.productThumbnailBaseUrl,
              path: x.attributes.productThumbnailUrl?.productThumbnailName,
            }),
        title: product.productName,
      }));

      // remove duplicate images, since there may already be a variation in allImages
      allImages = Array.from(new Map([...allImages, ...parsedImages].map((item) => [item.img, item])).values());
    }

    setImagesGallery(allImages);
    setImageToDisplay(0);
  }, [
    product.variations,
    product.productName,
    product?.productImageUrl,
    product.productThumbnailUrl?.productThumbnailName,
    product.productThumbnailUrl?.productThumbnailBaseUrl,
    isMobile,
  ]);

  useEffect(() => setIsHydrated(true), []);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
        },
        gap: GAP,
      }}
    >
      <ProductDetailImageGallery
        sx={{
          [theme.breakpoints.up('md')]: {
            width: '45%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: '1rem',
          },
        }}
        loading={!isHydrated}
        product={product}
        productWSP={productWSP}
        images={imagesGallery}
        imageToDisplay={imageToDisplay}
        babyGlideRef={babyGlideRef}
      />
      <ProductDetailInfo
        sx={{
          [theme.breakpoints.up('md')]: {
            width: `calc(55% - ${GAP})`, // should be same value
          },
        }}
        loading={loading}
        product={product}
        onVariationChange={onVariationChange}
      />
    </Box>
  );
};

ProductDetail.propTypes = {
  loading: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    productName: PropTypes.string,
    productThumbnailUrl: PropTypes.shape({
      productThumbnailName: PropTypes.string,
      productThumbnailBaseUrl: PropTypes.string,
    }),
    productImageUrl: PropTypes.arrayOf(
      PropTypes.shape({
        productImageName: PropTypes.string,
        productImageBaseUrl: PropTypes.string,
      })
    ),
    variations: PropTypes.arrayOf(
      PropTypes.shape({
        attributes: PropTypes.arrayOf(
          PropTypes.shape({
            productThumbnailUrl: PropTypes.shape({
              productThumbnailName: PropTypes.string,
              productThumbnailBaseUrl: PropTypes.string,
            }),
          })
        ),
      })
    ),
  }).isRequired,
  productWSP: PropTypes.number,
};

export default ProductDetail;
